import { useCallback, useEffect, useRef, useState } from 'react';
import BackButton from '../../components/Button/IconButton/BackButton';
import { MinigameState } from '../minigame_state_defs';
import PlayingGame from './PlayingGame';
import ScoreBar from './ScoreBar';
import { SockColor, missingColors, sockFrequency } from './laundry_game_defs';
import DialogueBox from '../../components/DialogueBox/DialogueBox';
import { useSoundEffect } from '../../hooks/useSoundEffect';

import CLOTH_DROP from '../../assets/audio/cloth_drop.wav';
import SAD_TROMBONE_SOUND from '../../assets/audio/sad_trombone.wav';
import SCORE_SOUND from '../../assets/audio/success.mp3';
import WIN_SOUND from '../../assets/audio/win.wav';

interface LaundryGameProps {
    goToLandingPage: () => void;
    markGameDoneCb: () => void;
}

function colorIsValid(caughtSocks: SockColor[], newSock: SockColor): boolean {
    const isNewPair =
        caughtSocks.length === 0 ||
        (caughtSocks.length >= 2 &&
            caughtSocks[caughtSocks.length - 1] ===
                caughtSocks[caughtSocks.length - 2]);
    if (isNewPair) {
        const missing = missingColors(caughtSocks);
        return missing.includes(newSock);
    } else {
        return caughtSocks[caughtSocks.length - 1] === newSock;
    }
}

function missingColor(caughtSocks: SockColor[]): SockColor {
    return Array.from(sockFrequency(caughtSocks).entries()).filter(
        ([_color, count]) => count === 1
    )[0][0];
}

function LaundryGame(props: LaundryGameProps) {
    const { goToLandingPage, markGameDoneCb } = props;
    const [minigameState, setMinigameState] = useState(MinigameState.INTRO);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [caughtSocks, setCaughtSocks] = useState<SockColor[]>([]);
    const [showWinDialogue, setShowWinDialogue] = useState(false);

    const { play: clothDropSound } = useSoundEffect(CLOTH_DROP, 0.3);
    const { play: scoreSound } = useSoundEffect(SCORE_SOUND);
    const { play: sadTromboneSound } = useSoundEffect(SAD_TROMBONE_SOUND, 0.3);
    const { play: winSound } = useSoundEffect(WIN_SOUND);

    const caughtSock = useCallback(
        (color: SockColor) => {
            clothDropSound();
            setCaughtSocks((caughtSocks) => {
                if (colorIsValid(caughtSocks, color)) {
                    caughtSocks.push(color);
                    if (caughtSocks.length % 2 === 0) {
                        scoreSound();
                    }
                } else {
                    caughtSocks[caughtSocks.length - 1] = color;
                }

                return [...caughtSocks];
            });
        },
        [clothDropSound, scoreSound]
    );

    useEffect(() => {
        if (
            missingColors(caughtSocks).length === 1 &&
            caughtSocks[caughtSocks.length - 1] !==
                caughtSocks[caughtSocks.length - 2]
        ) {
            setMinigameState(MinigameState.WON);
            winSound();
            setTimeout(() => {
                setShowWinDialogue(true);
            }, 1250);
        }
    }, [caughtSocks, winSound]);

    const startGame = useCallback(
        () => setMinigameState(MinigameState.PLAYING),
        []
    );

    const gameBounds = wrapperRef.current?.getBoundingClientRect();

    return (
        <div className="laundry-game" ref={wrapperRef}>
            <BackButton onClick={goToLandingPage} />
            <ScoreBar caughtSocks={caughtSocks} />
            {minigameState === MinigameState.INTRO && (
                <DialogueBox
                    text={[
                        'Be positive... laundry time, so fun!',
                        "(Make pairs of socks by catching the same sock twice in a row.\nThe sock with the single red slash is the sock you're seeking the pair for.)",
                    ]}
                    afterDialogueCb={startGame}
                />
            )}
            {minigameState === MinigameState.PLAYING &&
                gameBounds !== undefined && (
                    <PlayingGame
                        gameBounds={gameBounds}
                        caughtSocks={caughtSocks}
                        caughtSock={caughtSock}
                    />
                )}
            {showWinDialogue && (
                <DialogueBox
                    text={["Thank you, I'm finally done folding my laundry!"]}
                    afterDialogueCb={() => {
                        setMinigameState(MinigameState.SAD_AGAIN);
                        sadTromboneSound();
                    }}
                />
            )}
            {minigameState === MinigameState.SAD_AGAIN && (
                <DialogueBox
                    text={[
                        `Uh oh, looks like I lost a ${SockColor[missingColor(caughtSocks)].toLowerCase()} sock. I'll never find it again!`,
                    ]}
                    afterDialogueCb={(): void => {
                        markGameDoneCb();
                        goToLandingPage();
                    }}
                />
            )}
        </div>
    );
}

export default LaundryGame;
